/**
 * Determines if a location qualifies as "Finland" for job filtering purposes.
 * 
 * A location qualifies as Finland if:
 * - It explicitly mentions Finland (e.g., "Helsinki, Finland", "Finland", "Suomi")
 * - It's "Remote - Finland" (or language equivalents)
 * - It's a regional remote option that could include Finland (e.g., "Remote - Global", "Remote - Europe", "Remote - EMEA")
 * - It's standalone "Remote" (without a specific country/region suffix)
 * 
 * A location does NOT qualify if:
 * - It's "Remote - <specific country>" where the country is not Finland (e.g., "Remote - Estonia", "Remote - United States")
 * 
 * @param location - The location string to check
 * @returns true if the location qualifies as Finland, false otherwise
 */
export function isFinlandLocation(location: string | undefined | null): boolean {
  if (!location || typeof location !== 'string') return false;
  
  const normalized = location.toLowerCase().trim();
  
  // Anything that explicitly mentions Finland
  if (normalized.includes('finland') || normalized.includes('suomi')) {
    return true;
  }
  
  // Remote prefixes in different languages
  const remotePrefixes = [
    'remote -',
    'etätyö -',
    'etä -',
    'distans -',
    'fjärr -'
  ];
  
  // Check if it starts with a remote prefix
  const hasRemotePrefix = remotePrefixes.some(prefix => normalized.startsWith(prefix));
  
  if (hasRemotePrefix) {
    // Extract the part after "Remote -" (or equivalent)
    const afterRemote = normalized.replace(/^(remote|etätyö|etä|distans|fjärr)\s*-\s*/i, '').trim();
    
    // If it's "Remote - Finland" (or language equivalents)
    if (afterRemote.includes('finland') || afterRemote.includes('suomi')) {
      return true;
    }
    
    // Regional options that could include Finland
    const regionalOptions = [
      'global',
      'globaali',
      'globalt',
      'worldwide',
      'world',
      'europe',
      'eurooppa',
      'europa',
      'eu',
      'emea',
      'mena',
      'nordics',
      'nordic',
      'pohjoismaat',
      'scandinavia',
      'skandinavien',
      'baltics',
      'baltic',
      'benelux',
      'north america',
      'asia',
      'africa',
      'oceania',
      'south america',
      'anywhere'
    ];
    
    // Check if it matches a regional option
    if (regionalOptions.some(region => afterRemote === region || afterRemote.startsWith(region + ' '))) {
      return true;
    }
    
    // If it's just "Remote" (standalone, no suffix), include it
    if (afterRemote === '') {
      return true;
    }
    
    // Otherwise, it's "Remote - <specific country>" which should NOT be included
    // unless that country is Finland (already checked above)
    return false;
  }
  
  // Standalone "Remote" (without prefix format)
  if (normalized === 'remote' || 
      normalized === 'etätyö' || 
      normalized === 'distans' || 
      normalized === 'fjärr') {
    return true;
  }
  
  // "Anywhere" variations
  if (normalized.includes('anywhere')) {
    return true;
  }
  
  return false;
}

/**
 * Determines if a job qualifies as "Finland" based on its location_en array.
 * 
 * @param job - Job object with location_en array
 * @returns true if any location in the array qualifies as Finland, false otherwise
 */
export function isFinlandJob(job: { location_en?: string[] }): boolean {
  if (!job.location_en || !Array.isArray(job.location_en)) return false;
  return job.location_en.some(loc => isFinlandLocation(loc));
}

/**
 * Strict check: Only includes locations that explicitly mention "Finland" or "Suomi".
 * Excludes remote-global jobs like "Remote - Europe", "Remote - Global", etc.
 * 
 * This is used for calculating Finland-wide salary and trend aggregates to ensure
 * they only include jobs explicitly in Finland, not remote jobs available globally.
 * 
 * @param location - The location string to check
 * @returns true if the location explicitly mentions Finland/Suomi, false otherwise
 */
export function isFinlandLocationStrict(location: string | undefined | null): boolean {
  if (!location || typeof location !== 'string') return false;
  
  const normalized = location.toLowerCase().trim();
  
  // Only include locations that explicitly mention Finland or Suomi
  // This includes:
  // - "Helsinki, Finland", "Tampere, Finland", etc.
  // - "Finland", "Suomi"
  // - "Remote - Finland", "Etätyö - Suomi" (remote jobs specifically for Finland)
  // 
  // This excludes:
  // - "Remote - Global", "Remote - Europe", "Remote - EMEA", etc.
  // - Standalone "Remote" without Finland mention
  
  // Check if it explicitly mentions Finland or Suomi
  if (normalized.includes('finland') || normalized.includes('suomi')) {
    // Make sure it's not "Remote - <something else>" that happens to contain "finland" in the wrong place
    // For example, we want "Remote - Finland" but not "Remote - Finland, Estonia" (though that's unlikely)
    return true;
  }
  
  return false;
}

/**
 * Strict check: Determines if a job qualifies as "Finland" based on its location_en array.
 * Only includes jobs where at least one location explicitly mentions "Finland" or "Suomi".
 * Excludes remote-global jobs.
 * 
 * @param job - Job object with location_en array
 * @returns true if any location in the array explicitly mentions Finland/Suomi, false otherwise
 */
export function isFinlandJobStrict(job: { location_en?: string[] }): boolean {
  if (!job.location_en || !Array.isArray(job.location_en)) return false;
  return job.location_en.some(loc => isFinlandLocationStrict(loc));
}
